import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AddressCoords, Company } from '@core/interfaces';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { Apollo } from 'apollo-angular';
import { getDistance } from 'geolib';
import gql from 'graphql-tag';
import { chain } from 'lodash';
import { sentry } from 'sentry';
import { FcSearchInputComponent } from '../../base/fc-search-input/fc-search-input.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

const SEARCH_QUERY = gql`
  query searchCompanies(
    $where: CompanyWhereInput
    $orderByDistance: CompanyOrderByDistanceInput
  ) {
    companies(
      where: $where
      orderByDistance: $orderByDistance
      orderBy: name_asc
      limit: 1000
      offset: 0
    ){
      items {
        id
        name
        avatarUrl
        createdAt
        archived
        address {
          coords {
            latitude
            longitude
          }
        }
      }
    }
  }
`;

@Component({
  selector: 'fc-company-input',
  templateUrl: './fc-company-input.component.html',
  styleUrls: ['./fc-company-input.component.scss']
})
export class FcCompanyInputComponent implements OnChanges {
  @Input()
  public control: FormControl;

  @Input()
  public compareWithCoords: AddressCoords = null;

  @Input()
  public showSearchIcon = false;

  @Input()
  public requiredMark = false;

  @Input()
  public preloadOptions = true;

  @Input()
  public searchOnFocus = false;

  public displayText: string;

  @ViewChild('fcSearchInput', { static: true }) searchInput: FcSearchInputComponent;

  constructor(
    private readonly apollo: Apollo,
    private readonly translateService: TranslateService,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
    this.search = this.search.bind(this);
    this.displayWith = this.displayWith.bind(this);
  }

  resetSearch(): void {
    this.searchInput.resetSearch();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.compareWithCoords && changes.compareWithCoords.currentValue && this.control.value) {
      this.displayText = this.displayWith(this.control.value);
    }
  }

  async search(searchTerm: string): Promise<Company[]> {
    return this.apollo
      .query({
        query: SEARCH_QUERY,
        variables: {
          where: {
            name_contains: searchTerm,
            archived: false
          },
          orderByDistance: this.compareWithCoords && {
            direction: 'asc',
            distance: {
              latitude: Number(this.compareWithCoords.latitude),
              longitude: Number(this.compareWithCoords.longitude)
            }
          }
        }
      })
      .toPromise()
      .then((result) => {
        const { items } = result.data['companies'];
        return this.sortCompanies(items);
      })
      .catch((err) => {
        sentry.captureException(err);
        return [];
      });
  }

  displayWith(value: Company): string {
    try {
      const { address } = value;

      if (this.compareWithCoords && address && address.coords.latitude && address.coords.longitude) {
        const distanceInMeters = getDistance(this.compareWithCoords, address.coords);
        const distanceFromMaintenance = this.translateService.instant('COMPANY.DISTANCE', {
          distance: (distanceInMeters / 1000).toFixed(2).replace('.', ',')
        });

        return `${value.name} - (${distanceFromMaintenance})`;
      }

      return value.name;
    } catch {
      return null;
    }
  }

  sortCompanies(items: Company[]): Company[] {
    if (!this.compareWithCoords) {
      return items;
    }

    return chain(items)
      .map((item) => ({
        ...item,
        distance: item.address.coords.latitude && item.address.coords.longitude && getDistance(this.compareWithCoords, item.address.coords)
      }))
      .value();
  }
}
